// Copyright 2020 TOKU Systems Inc.

import React, { useState, useEffect } from 'react';

import illumass from '../../services/illumass';
import AppBarWithMenu from '../app-bar-with-menu';
import { Container } from '@material-ui/core';
import MaterialTable from 'material-table';
import { ApiKeyRecord, ApiKey } from './common';
import AddApiKeyDialog from './add-api-key-dialog';
import ShowApiKeyDialog from './show-api-key-dialog';
import { useSnackbar } from 'notistack';
import moment from 'moment-timezone';
import * as _ from 'lodash';

const timezone = moment.tz.guess();

const toRecord = (doc: {
  id: string;
  userId: string;
  expiresAt: number;
  ttl: number;
  description: string;
  fingerprint: string;
  token?: string;
}): ApiKeyRecord => {
  const apiKey = new ApiKey(
    doc.id,
    doc.userId,
    doc.expiresAt,
    doc.ttl,
    doc.description,
    doc.fingerprint,
    doc.token
  );

  if (apiKey.ttl.asMilliseconds() < 0) {
    return {
      id: apiKey.id,
      description: apiKey.description,
      fingerprint: apiKey.fingerprint,
      issuedAt: 'N/A',
      issuedAtMillis: apiKey.issuedAt.valueOf(),
      expiresAt: 'Never',
      expiresAtMillis: apiKey.expiresAt.valueOf(),
      token: apiKey.token,
    };
  } else {
    return {
      id: apiKey.id,
      description: apiKey.description,
      fingerprint: apiKey.fingerprint,
      issuedAt: apiKey.issuedAt
        .clone()
        .tz(timezone)
        .format('MMMM D YYYY, h:mm:ss a'),
      issuedAtMillis: apiKey.issuedAt.valueOf(),
      expiresAt: apiKey.expiresAt
        .clone()
        .tz(timezone)
        .format('MMMM D YYYY, h:mm:ss a'),
      expiresAtMillis: apiKey.expiresAt.valueOf(),
      token: apiKey.token,
    };
  }
};

export default function ApiKeys(): JSX.Element {
  const [openAdd, setOpenAdd] = useState(false);
  const [rows, setRows] = useState<ApiKeyRecord[]>([]);
  const [reload, setReload] = useState(true);
  const [backdropLevel, setBackdropLevel] = useState(0);
  const [showApiKeyRecord, setShowApiKeyRecord] = useState<
    ApiKeyRecord | undefined
  >(undefined);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (reload) {
      setReload(false);
      const loadApiKeys = async (): Promise<void> => {
        setBackdropLevel(backdropLevel + 1);
        try {
          const results = await illumass.auth.searchApiKeys();
          setRows(results.hits.map((hit) => toRecord(hit)));
        } catch (err) {
          enqueueSnackbar(_.get(err, ['message'], 'Failed to load API keys'), {
            variant: 'error',
          });
        } finally {
          setBackdropLevel(Math.max(backdropLevel - 1, 0));
        }
      };

      loadApiKeys();
    }
  }, [reload, backdropLevel, enqueueSnackbar]);

  const handleAddClick = (): void => {
    // Add api key
    setOpenAdd(true);
  };

  const handleAddApiKey = (description: string, ttl: number): void => {
    setBackdropLevel(backdropLevel + 1);
    illumass.auth
      .createApiKey(description.trim(), {
        expiresIn: ttl,
        refresh: true,
      })
      .then((result) => {
        // Need to display
        setShowApiKeyRecord(toRecord(result));
        setReload(true);
        setBackdropLevel(Math.max(backdropLevel - 1, 0));
        setOpenAdd(false);
      })
      .catch((err) => {
        enqueueSnackbar(_.get(err, ['message'], 'Unable to create API key.'), {
          variant: 'error',
        });
        setBackdropLevel(Math.max(backdropLevel - 1, 0));
      });
  };

  const handleCancelAddApiKey = (): void => {
    setOpenAdd(false);
  };

  const handleDetailsClick = (
    _event: unknown,
    recordsToRevoke: ApiKeyRecord | ApiKeyRecord[]
  ): void => {
    if (_.isArray(recordsToRevoke)) {
      if (recordsToRevoke.length > 0) {
        setShowApiKeyRecord(recordsToRevoke[0]);
      }
    } else {
      setShowApiKeyRecord(recordsToRevoke);
    }
  };

  const handleRevokeClick = (
    _event: unknown,
    recordsToRevoke: ApiKeyRecord | ApiKeyRecord[]
  ): void => {
    // Revoke api key
    setBackdropLevel(backdropLevel + 1);
    const records: ApiKeyRecord[] = [];

    if (_.isArray(recordsToRevoke)) {
      records.push(...recordsToRevoke);
    } else {
      records.push(recordsToRevoke);
    }

    _.reduce(
      records,
      (p, record) =>
        p
          .then(() => illumass.auth.deleteApiKey(record.id, { refresh: true }))
          .then(() => Promise.resolve()),
      Promise.resolve()
    )
      .then(() => setReload(true))
      .then(() => setBackdropLevel(Math.max(backdropLevel - 1, 0)))
      .catch((err) => {
        enqueueSnackbar(_.get(err, ['message'], 'Unable to delete API key.'), {
          variant: 'error',
        });
        setBackdropLevel(Math.max(backdropLevel - 1, 0));
      });
  };

  const handleCloseShowApiKeyRecord = (): void => {
    setShowApiKeyRecord(undefined);
  };

  return (
    <React.Fragment>
      <AppBarWithMenu title="Manage API keys" />
      <Container>
        <MaterialTable
          isLoading={backdropLevel > 0}
          title="API keys"
          options={{
            search: false,
            paging: false,
            showTitle: false,
            actionsColumnIndex: 3,
          }}
          localization={{
            header: {
              actions: 'Actions',
            },
            body: {
              emptyDataSourceMessage: 'No API keys',
            },
          }}
          columns={[
            { title: 'Description', field: 'description', editable: 'never' },
            {
              title: 'Issued at',
              field: 'issuedAt',
              editable: 'never',
              customSort: (a, b): number => a.issuedAtMillis - b.issuedAtMillis,
            },
            {
              title: 'Expires at',
              field: 'expiresAt',
              editable: 'never',
              customSort: (a, b): number =>
                a.expiresAtMillis - b.expiresAtMillis,
            },
          ]}
          data={rows}
          actions={[
            {
              icon: 'add',
              tooltip: 'Add API key',
              isFreeAction: true,
              onClick: handleAddClick,
            },
            {
              icon: 'details',
              tooltip: 'Details',
              onClick: handleDetailsClick,
            },
            {
              icon: 'delete',
              tooltip: 'Revoke API key',
              onClick: handleRevokeClick,
            },
          ]}
        />
      </Container>
      {openAdd && (
        <AddApiKeyDialog
          open={openAdd}
          onAddApiKey={handleAddApiKey}
          onCancel={handleCancelAddApiKey}
        />
      )}
      {showApiKeyRecord !== undefined && (
        <ShowApiKeyDialog
          record={showApiKeyRecord}
          onClose={handleCloseShowApiKeyRecord}
        />
      )}
    </React.Fragment>
  );
}
